import { useEffect } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronLeftIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface Props {
  title: ReactNode;
  /** One or two sentences: what this screen answers. */
  description?: ReactNode;
  /** Right-aligned; the primary action last, so it sits in the corner. */
  actions?: ReactNode;
  /** A state pill or a short fact beside the title. */
  meta?: ReactNode;
  /** The way back to the list this page belongs under. */
  back?: { to: string; label: string };
  /** A small line above the title: today's date on the overview. */
  eyebrow?: ReactNode;
  /** The browser tab's text when the title is not a plain string. */
  documentTitle?: string;
  className?: string;
}

/** The top of every screen: what it is, what it answers, what can be done.
 * A large title (UI-8), the back link as a blue chevron above it, and the
 * actions on the right, sitting on the description's baseline. */
export function PageHeader({
  title,
  description,
  actions,
  meta,
  back,
  eyebrow,
  documentTitle,
  className,
}: Props) {
  const tabTitle = documentTitle ?? (typeof title === "string" ? title : null);
  useEffect(() => {
    if (tabTitle === null) return;
    document.title = `${tabTitle} · Email marketing`;
  }, [tabTitle]);

  return (
    <header data-slot="page-header" className={cn("mb-6 flex flex-col gap-1", className)}>
      {back && (
        <Link
          to={back.to}
          className="-ml-1 mb-1 inline-flex w-fit items-center gap-0.5 text-body font-medium text-blue-ink hover:no-underline hover:opacity-80"
        >
          <ChevronLeftIcon className="size-4.5" strokeWidth={2.2} />
          {back.label}
        </Link>
      )}
      {eyebrow && <p className="m-0 text-label font-semibold text-muted-foreground uppercase">{eyebrow}</p>}
      <div className="flex flex-wrap items-end justify-between gap-x-6 gap-y-3">
        <div className="flex min-w-0 flex-col gap-1.5">
          <div className="flex min-w-0 flex-wrap items-center gap-3">
            <h1 className="m-0 min-w-0 break-words">{title}</h1>
            {meta}
          </div>
          {description && (
            <div className="max-w-[46rem] text-body text-muted-foreground">{description}</div>
          )}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
}
